/**
 * The conversation in a side panel over Zadania (boards P1–P8): the same 604px frame and right-edge
 * arrival as the builder's day dock, so the page behind it stays where it is.
 *
 * The header names the client and their plan, says how many OTHER conversations are waiting (and
 * leads to the inbox), offers „Otwórz w Czacie" — the full screen of this conversation, the draft
 * carried by the browser's draft store — and closes. Picking another person in the queue swaps the
 * conversation without closing the panel; the body cross-fades because the pane is keyed by it.
 *
 * Escape: the pane takes the first one if its popover is open; otherwise the panel closes.
 */

import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import dock from '~/shared/ui/DayDockFrame.module.css';

import { useConversations } from '../lib/useChatReads';
import { isReconnecting, useRealtimeStatus } from '../lib/chatSessionContext';

import { ChatAvatar } from './ChatAvatar';
import { ClockGlyph, CloseGlyph, SplitGlyph } from './ChatGlyphs';
import { ConversationPane } from './ConversationPane';
import { openFailureKey, type OpenFailure } from '../model/openFailure';
import styles from './chat.module.css';

export interface ChatDockProps {
  /** The conversation to show, or `null` while it is being opened (the panel waits in its frame). */
  readonly conversationId: number | null;
  /** The plan line under the name, as the host knows it. */
  readonly subline: string;
  /** What the inbox showed as unread for this conversation when the panel was asked to open it. */
  readonly unreadAtOpen: number;
  readonly opening: boolean;
  /** Why the conversation could not be opened, or `null`. */
  readonly failure: OpenFailure | null;
  readonly onClose: () => void;
  readonly onOpenClip: (customerId: number, clipId: number) => void;
}

export function ChatDock({ conversationId, subline, unreadAtOpen, opening, failure, onClose, onOpenClip }: ChatDockProps) {
  const { t } = useTranslation();
  const { rows } = useConversations();
  const status = useRealtimeStatus();
  const conversation = conversationId === null ? null : (rows.find((row) => row.id === conversationId) ?? null);
  const others = rows.filter((row) => row.unread > 0 && row.id !== conversationId).length;
  const reconnecting = isReconnecting(status);
  const frame = useRef<HTMLElement>(null);

  // Escape closes the panel wherever the keyboard is — a panel still opening (or one that could not
  // open) has no field to hold focus, so the press would otherwise land on the desk and do nothing.
  // Inside the panel the pane answers first: its popover takes the first press.
  // The frame is also where focus goes when the panel opens with no field to take it.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target instanceof Element ? event.target : null;
      // A dialog over the page (the lightbox, a confirm) answers its own Escape; the pane marks the
      // press it used for its popover as handled.
      if (target?.closest('[role="dialog"]') != null) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const waiting = conversation === null;
  useEffect(() => {
    if (waiting) frame.current?.focus();
  }, [waiting]);

  return (
    <aside
      ref={frame}
      tabIndex={-1}
      className={`${dock.host} ${dock.panel} ${styles.dock}`}
      aria-label={t('web:chat.dock.label')}
      // Where the Zadania desk's document-level keys stop: inside, the keys are the conversation's.
      data-chat-dock=""
    >
      {conversation === null ? (
        <div className={styles.dockWaiting} aria-busy={opening}>
          <div className={styles.dockHead}>
            <span className={styles.dockName} />
            <button className={styles.iconButton} type="button" onClick={onClose} aria-label={t('web:chat.dock.close')} title={t('web:chat.dock.closeHint')}>
              <CloseGlyph size={18} />
            </button>
          </div>
          {failure === null ? null : <div className={styles.threadState}>{t(openFailureKey(failure))}</div>}
        </div>
      ) : (
        <>
          <header className={styles.dockHead}>
            <ChatAvatar initials={conversation.initials} avatarUrl={conversation.avatarUrl} size={40} />
            <div className={styles.dockWho}>
              <div className={styles.dockName}>{conversation.name}</div>
              {reconnecting ? (
                <div className={`${styles.dockSub} ${styles.wait}`}>
                  <ClockGlyph size={12} width={2} />
                  {t('chat.window.reconnecting')}
                </div>
              ) : (
                <div className={styles.dockSub}>{subline}</div>
              )}
            </div>
            {others > 0 ? (
              <Link className={styles.others} to="/czat">
                <i aria-hidden="true" />
                {t('web:chat.dock.othersWaiting', { count: others })}
              </Link>
            ) : null}
            <Link
              className={styles.iconButton}
              to="/czat"
              search={{ c: conversation.id }}
              aria-label={t('web:chat.dock.openInChat')}
              title={t('web:chat.dock.openInChat')}
            >
              <SplitGlyph />
            </Link>
            <button className={styles.iconButton} type="button" onClick={onClose} aria-label={t('web:chat.dock.close')} title={t('web:chat.dock.closeHint')}>
              <CloseGlyph size={18} />
            </button>
          </header>
          <ConversationPane
            key={conversation.id}
            conversation={conversation}
            unreadAtOpen={unreadAtOpen}
            variant="dock"
            autoFocus
            onEscape={onClose}
            onOpenClip={(clipId) => {
              if (conversation.customerId !== null) onOpenClip(conversation.customerId, clipId);
            }}
          />
        </>
      )}
    </aside>
  );
}
